import {
  MailQuestion,
  CalendarCheck,
  NotebookPen,
  Compass,
  Users,
  Wand2,
  ImageOff,
  ScanSearch,
  Palette,
  SlidersHorizontal,
  Mail,
  Gift,
} from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AnimatedIconBadge } from '@/components/howItWorks/AnimatedIconBadge';
import { keyedList } from '@/lib/keys';
import { HOW_IT_WORKS } from '@/constants/testIds';
import { STAGE_CONTENT, STAGE_ICON_NAMES } from '@/lib/howItWorksContent';

const ICONS = {
  MailQuestion,
  CalendarCheck,
  NotebookPen,
  Compass,
  Users,
  Wand2,
  ImageOff,
  ScanSearch,
  Palette,
  SlidersHorizontal,
  Mail,
  Gift,
};

export const TimelineStageRow = ({ stage, index, audience, isActive, registerRef }) => {
  const content = STAGE_CONTENT[stage.slug][audience];
  const icons = STAGE_ICON_NAMES[stage.slug];
  const TraditionalIcon = ICONS[icons.traditional];
  const LxraIcon = ICONS[icons.lxra];

  return (
    <div
      ref={registerRef}
      data-stage-slug={stage.slug}
      className="relative grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-4 py-9 lg:py-12 border-b border-[hsl(var(--lxra-border))] last:border-b-0"
      data-testid={HOW_IT_WORKS.stageRow(stage.slug)}
    >
      <div className="lg:col-span-12 mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="lxra-mono text-xs text-[hsl(var(--lxra-muted))]">{String(index + 1).padStart(2, '0')}</span>
          <p className="text-xs tracking-[0.26em] uppercase text-[hsl(var(--lxra-muted))]">{stage.title}</p>
        </div>
        {stage.toolBadges?.length > 0 && (
          <div className="hidden sm:flex items-center gap-2">
            {stage.toolBadges.map((t) => (
              <Badge
                key={t}
                className="bg-[hsl(var(--lxra-gold)/0.14)] text-[hsl(var(--lxra-cream))] border border-[hsl(var(--lxra-gold)/0.35)] font-normal"
              >
                {t}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <Reveal className="lg:col-span-5" y={14}>
        <Card
          data-testid={HOW_IT_WORKS.traditionalCard(stage.slug)}
          className="relative h-full border-[hsl(var(--lxra-border))] bg-card"
        >
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AnimatedIconBadge icon={TraditionalIcon} variant="traditional" />
              <div className="flex-1 min-w-0">
                <Badge variant="secondary" className="mb-3">Traditional</Badge>
                <h3 className="text-sm font-semibold text-[hsl(var(--lxra-fg))]">{content.traditional.title}</h3>
                <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">{content.traditional.body}</p>
                <ul className="mt-3 space-y-2 text-sm text-[hsl(var(--lxra-muted))]">
                  {keyedList(content.traditional.bullets).map(({ key, item }) => (
                    <li key={key} className="flex gap-2">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-[hsl(var(--lxra-muted))] shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </Reveal>

      <div className="hidden lg:flex lg:col-span-2 items-start justify-center pt-1">
        <span
          data-testid={HOW_IT_WORKS.stageNode(stage.slug)}
          className={`h-2.5 w-2.5 rounded-full border transition-colors duration-200 ${
            isActive
              ? 'bg-[hsl(var(--lxra-gold))] border-[hsl(var(--lxra-gold))]'
              : 'bg-[hsl(var(--lxra-bg))] border-[hsl(var(--lxra-gold)/0.55)]'
          }`}
        />
      </div>

      <Reveal className="lg:col-span-5" y={14} delay={0.06}>
        <Card
          data-testid={HOW_IT_WORKS.lxraCard(stage.slug)}
          className={`relative h-full bg-card border-t-4 lg:border-t-0 lg:border-l-4 border-[hsl(var(--lxra-gold)/0.35)] transition-shadow duration-200 ${
            isActive ? 'ring-1 ring-[hsl(var(--lxra-gold)/0.45)]' : ''
          }`}
        >
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AnimatedIconBadge icon={LxraIcon} variant="lxra" />
              <div className="flex-1 min-w-0">
                <Badge className="mb-3 bg-[hsl(var(--lxra-gold)/0.14)] text-[hsl(var(--lxra-cream))] border border-[hsl(var(--lxra-gold)/0.35)]">
                  LXRA AI-Powered
                </Badge>
                <h3 className="text-sm font-semibold text-[hsl(var(--lxra-fg))]">{content.lxra.title}</h3>
                <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">{content.lxra.body}</p>
                <ul className="mt-3 space-y-2 text-sm text-[hsl(var(--lxra-muted))]">
                  {keyedList(content.lxra.bullets).map(({ key, item }) => (
                    <li key={key} className="flex gap-2">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-[hsl(var(--lxra-gold))] shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </Reveal>
    </div>
  );
};
